import { parse as parseYaml } from 'yaml';

/**
 * Example files and `action.yml`, vendored from the action repository and
 * inlined at build time by Vite's `?raw` loader.
 *
 * They stay as real `.yaml` files rather than TypeScript string literals so
 * they diff cleanly, keep editor highlighting, and can be refreshed with
 * `scripts/sync-examples.sh`.
 */

export interface ExampleFile {
  id: string;
  name: string;
  body: string;
  blurb: string;
}

const MANIFEST_BLURBS: Record<string, string> = {
  'service-minimal.yaml':
    'The smallest useful service. Roles, security group, scaling and log retention are all defaulted.',
  'service-full.yaml':
    'A production service using most of what the manifest offers — imported roles, ARM64, tags, request-count scaling.',
  'service-worker.yaml':
    'A background worker: long-running, no load balancer, so no listener, target group or health check.',
  'scheduled-tasks.yaml':
    'Cron jobs. One EventBridge rule per entry; jobs sharing the default size share a task definition.',
};

const WORKFLOW_BLURBS: Record<string, string> = {
  'deploy.yml': 'The smallest useful workflow: deploy on push to main, authenticating with OIDC.',
  'iam-access-keys.yml':
    'Static IAM access keys instead of OIDC, deploying a single staging environment.',
  'deploy-multi-environment.yml':
    'Development then production, promoting the exact image rather than rebuilding it.',
  'pull-request-diff.yml':
    'Post the CloudFormation diff as a pull request comment, using a read-only role.',
  'scheduled-tasks.yml': 'Deploying a ScheduledTasks manifest.',
  'reusable-workflow-with-secrets.yml':
    'Calling the workflow_call wrapper, where AWS credentials arrive in a secrets: block.',
};

function collect(
  modules: Record<string, string>,
  blurbs: Record<string, string>,
  order: string[],
): ExampleFile[] {
  const files = Object.entries(modules).map(([path, body]) => {
    const name = path.split('/').pop()!;
    return { id: name.replace(/\.(yaml|yml)$/, ''), name, body, blurb: blurbs[name] ?? '' };
  });

  // Present them in teaching order, not alphabetical.
  return files.sort((a, b) => {
    const ai = order.indexOf(a.name);
    const bi = order.indexOf(b.name);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}

export const MANIFESTS = collect(
  import.meta.glob('./examples/manifests/*.yaml', { query: '?raw', import: 'default', eager: true }),
  MANIFEST_BLURBS,
  ['service-minimal.yaml', 'service-full.yaml', 'service-worker.yaml', 'scheduled-tasks.yaml'],
);

export const WORKFLOWS = collect(
  import.meta.glob('./examples/workflows/*.yml', { query: '?raw', import: 'default', eager: true }),
  WORKFLOW_BLURBS,
  [
    'deploy.yml',
    'iam-access-keys.yml',
    'deploy-multi-environment.yml',
    'pull-request-diff.yml',
    'scheduled-tasks.yml',
    'reusable-workflow-with-secrets.yml',
  ],
);

// -- action.yml -----------------------------------------------------------

import actionYaml from './examples/action.yml?raw';

export interface ActionInput {
  name: string;
  description: string;
  required: boolean;
  default?: string;
}

export interface ActionOutput {
  name: string;
  description: string;
}

/** Section headings recovered from the comment banners in action.yml. */
const INPUT_SECTIONS: Array<{ match: RegExp; title: string }> = [
  { match: /^(manifest|image|command)$/, title: 'What to deploy' },
  { match: /^(role-|aws-)/, title: 'AWS authentication' },
  {
    match: /^(ecr-|create-ecr|image-tag|push-latest|dockerfile|context|build-|platforms|provenance)/,
    title: 'Image build',
  },
];

const action = parseYaml(actionYaml) as any;

export const ACTION_NAME: string = action.name;
export const ACTION_DESCRIPTION: string = String(action.description).trim();

export const ACTION_INPUTS: ActionInput[] = Object.entries(action.inputs ?? {}).map(
  ([name, spec]: [string, any]) => ({
    name,
    description: String(spec.description ?? '').trim().replace(/\s+/g, ' '),
    required: spec.required === true,
    default: spec.default === '' ? undefined : String(spec.default ?? '') || undefined,
  }),
);

export const ACTION_OUTPUTS: ActionOutput[] = Object.entries(action.outputs ?? {}).map(
  ([name, spec]: [string, any]) => ({
    name,
    description: String(spec.description ?? '').trim().replace(/\s+/g, ' '),
  }),
);

export function inputSection(name: string): string {
  return INPUT_SECTIONS.find((s) => s.match.test(name))?.title ?? 'Deployment behaviour';
}
